import React from "react";
import Footer from "../../../components/Footer/Footer";
import {
  Brand,
  ContactBlock,
  Content,
  Header,
  HeroHeadline,
  List,
  PageWrapper,
  Paragraph,
  Section,
  SectionHeading,
  Strong,
  UpdatedDate,
} from "../Policy/styled";

const DesktopSupport: React.FC = () => {
  const updatedDate = "February 10, 2025";

  return (
    <PageWrapper>
      <Header>
        <Brand>Cabinet Logger</Brand>
        <HeroHeadline>Support</HeroHeadline>
        <UpdatedDate>Last updated {updatedDate}</UpdatedDate>
        <Paragraph>
          We are committed to keeping your teams scanning, syncing, and
          delivering without interruption. This page outlines the quickest ways
          to get help, the troubleshooting steps we recommend before you reach
          out, and what to include in a ticket so we can resolve issues fast.
        </Paragraph>
      </Header>

      <Content>
        <Section>
          <SectionHeading>Immediate Help</SectionHeading>
          <Paragraph>
            If you lose connectivity or encounter an authentication prompt after
            coming back online, use the steps below to restore access while we
            investigate with you:
          </Paragraph>
          <List>
            <li>
              <Strong>Refresh your session</Strong> by opening the menu and
              selecting <em>Log Out</em>, then signing back in. This forces
              Firebase to mint a new token if your offline session expired.
            </li>
            <li>
              <Strong>Manually sync pending scans</Strong> from the More tab.
              Tap <em>Sync Now</em> once you have a stable signal so queued
              updates can finish processing.
            </li>
            <li>
              <Strong>Capture a screenshot</Strong> of any error banner or toast
              message. Include the timestamp and whether you were offline prior
              to the issue.
            </li>
          </List>
        </Section>

        <Section>
          <SectionHeading>Contact Support</SectionHeading>
          <Paragraph>
            When you reach out, share the shipment ID, dealer name, the device
            you are using, and whether you were working offline. This context
            helps us reproduce and fix problems quickly.
          </Paragraph>
          <ContactBlock>
            <span>
              <Strong>Email</Strong>: Cristopher.blunt@cabinetscanner.com
            </span>
            <span>
              <Strong>Escalations</Strong>: support@cabinetlogger.com
            </span>
            <span>
              <Strong>Phone</Strong>: Phone support is scheduled for active
              accounts; include your call-back number in the ticket and we will
              reach out within business hours (Monday through Friday, 8am–6pm CT).
            </span>
          </ContactBlock>
        </Section>

        <Section>
          <SectionHeading>What We Monitor</SectionHeading>
          <Paragraph>
            Our support team watches platform health indicators around the clock
            so we can alert you when there is a known issue. We proactively
            review:
          </Paragraph>
          <List>
            <li>Authentication success rates from Firebase and custom claims</li>
            <li>Offline queue backlog size and retry counts</li>
            <li>API latency for shipment updates and scan uploads</li>
            <li>Crash analytics from iOS, Android, and the desktop web client</li>
          </List>
        </Section>

        <Section>
          <SectionHeading>Preparing a Diagnostic Report</SectionHeading>
          <Paragraph>
            For stubborn sync or authentication errors, send the diagnostic logs
            from your device. The steps differ slightly by platform:
          </Paragraph>
          <List>
            <li>
              <Strong>iOS or Android</Strong>: From the More tab, tap <em>Support</em>
              and choose <em>Export Logs</em>. Email the generated ZIP to our
              support inbox with a short description of what happened.
            </li>
            <li>
              <Strong>Desktop</Strong>: Press <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>I</kbd> (or <kbd>⌥</kbd> + <kbd>⌘</kbd> + <kbd>I</kbd> on
              macOS) to open the console. Save the network and console output as
              HAR and log files before closing the window.
            </li>
            <li>
              <Strong>Offline scanners</Strong>: Note the shipment numbers and
              timestamps visible in the queue. These help us align client logs
              with the server timeline.
            </li>
          </List>
        </Section>

        <Section>
          <SectionHeading>Status Updates</SectionHeading>
          <Paragraph>
            We will email all account administrators if there is a widespread
            incident affecting sign-in, syncing, or barcode scanning. Real-time
            notices also appear on the dashboard banner when you sign in.
          </Paragraph>
        </Section>
      </Content>

      <Footer />
    </PageWrapper>
  );
};

export default DesktopSupport;
